const { buildAtomicBoardStatCandidates } = require("./atomicBoardTransition.js");

const BOARD_SNAPSHOT_VERSION = 1;

// This is not a stale preview cache. Only current-request network results are
// admitted, and reads fail closed on missing evidence or a crossed start time.
const createRecentVerifiedBoardCache = ({ now = () => Date.now() } = {}) => {
  const entries = new Map();
  const isLive = (row) => row.is_live === true || row.is_live === 1 || row.is_live === "1";
  // Diagnostic codes: 0=not attempted, 1=hit, 2=missing key/entry,
  // 3=expired/clock rollback, 4=invalid evidence, 5=started/unknown start.
  const get = (key, onStatus = () => {}) => {
    const entry = entries.get(key);
    if (!entry) { onStatus(2); return null; }
    const time = now();
    const rows = entry.result.list;
    const ttl = rows.some(isLive) ? 5000 : 30000;
    let status = time >= entry.startedAt && time - entry.startedAt < ttl ? 1 : 3;
    if (status === 1) rows.every((row) => {
      const observed = Number(row.board_observed_at) * 1000;
      const start = Date.parse(row.start_time_iso || row.start_time_obj || "");
      if (row._boardOptimisticPreview || !row.board_snapshot_id || !(observed > 0 && observed <= time)) {
        status = 4;
        return false;
      }
        // Source observation is not the time this response was verified.
        // Unchanged provider snapshots can be older than the reuse window even
        // after the server has just accepted them. The short TTL above starts
        // at the force-fresh request, never at a cache read or source timestamp.
      if (!isLive(row) && !(Number.isFinite(start) && start > time)) {
        status = 5;
        return false;
      }
      return true;
    });
    onStatus(status);
    if (status !== 1) { entries.delete(key); return null; }
    return { ...entry.result, cached: true, verifiedCache: true,
      list: [...rows], rawList: [...entry.result.rawList] };
  };
  return {
    get,
    clear: (scope) => {
      if (!scope) { entries.clear(); return; }
      entries.forEach((entry, key) => {
        if (!entry.scope || entry.scope === scope) entries.delete(key);
      });
    },
    set: (key, result, startedAt, scope = "") => {
      if (!key || result?.failed || result?.aborted || result?.cached || result?.verifiedCache
        || !Number.isFinite(startedAt) || !result?.list?.length) return;
      entries.delete(key);
      entries.set(key, { startedAt, scope, result: { ...result,
        list: [...result.list], rawList: [...(result.rawList || result.list)] } });
      while (entries.size > 24) entries.delete(entries.keys().next().value);
    },
  };
};

const normalizeEvidenceText = (value) => String(value || "").trim().slice(0, 160);

const collectBoardFeedEvidence = (athletes = []) => {
  const evidenceByKey = new Map();

  (Array.isArray(athletes) ? athletes : []).forEach((athlete) => {
    const bundleId = normalizeEvidenceText(athlete?.board_bundle_id);
    const snapshotId = normalizeEvidenceText(athlete?.board_snapshot_id);
    const observedAt = Math.max(0, Number(athlete?.board_observed_at) || 0);
    if (!bundleId && !snapshotId) {
      return;
    }

    const key = `${bundleId}|${snapshotId}`;
    const existing = evidenceByKey.get(key);
    if (!existing || observedAt > existing.observedAt) {
      evidenceByKey.set(key, { bundleId, snapshotId, observedAt });
    }
  });

  return Array.from(evidenceByKey.values()).sort((left, right) => (
    `${left.bundleId}|${left.snapshotId}`.localeCompare(`${right.bundleId}|${right.snapshotId}`)
  ));
};

const getBoardSnapshotExpiresAt = (athletes = [], now = Date.now()) => {
  const futureStarts = (Array.isArray(athletes) ? athletes : [])
    .filter((athlete) => !athlete?.is_live)
    .map((athlete) => Date.parse(athlete?.start_time_iso || athlete?.start_time_obj || ""))
    .filter((startMs) => Number.isFinite(startMs) && startMs > now);

  return futureStarts.length > 0 ? Math.min(...futureStarts) : 0;
};

const createBoardSnapshot = ({
  requestToken,
  requestedSport = "",
  requestedStat = "",
  sport = "",
  stat = "",
  stats = {},
  sports = [],
  availableStats = {},
  athletes = [],
  metadataComplete = false,
  now = Date.now(),
} = {}) => {
  const athleteRows = Array.isArray(athletes) ? [...athletes] : [];
  const feedEvidence = collectBoardFeedEvidence(athleteRows);

  return Object.freeze({
    version: BOARD_SNAPSHOT_VERSION,
    requestToken,
    requestedSport: String(requestedSport || "").toLowerCase(),
    requestedStat: String(requestedStat || ""),
    sport: String(sport || "").toLowerCase(),
    stat: String(stat || ""),
    stats: Object.freeze({ ...(stats || {}) }),
    sports: Object.freeze([...(Array.isArray(sports) ? sports : [])]),
    availableStats: Object.freeze({ ...(availableStats || {}) }),
    athletes: Object.freeze(athleteRows),
    selectedGameId: "",
    selectedGame: null,
    evidence: Object.freeze({
      createdAt: now,
      expiresAt: getBoardSnapshotExpiresAt(athleteRows, now),
      metadataComplete: metadataComplete === true,
      feedSnapshots: Object.freeze(feedEvidence),
    }),
  });
};

const createBoardDataSource = ({
  fetchStatsSelection,
  fetchAthletes,
  getRecentVerifiedAthletes = () => null,
  isRequestCurrent,
  normalizeSport,
  getInitialAthleteLimit,
  getVerifiedStats,
  getCurrentSports,
  maxStatProbes = 3,
  excludedStats = [],
  now = () => Date.now(),
} = {}) => {
  if (typeof fetchStatsSelection !== "function" || typeof fetchAthletes !== "function") {
    throw new TypeError("BoardDataSource requires stats and athlete fetchers.");
  }

  const isCurrent = typeof isRequestCurrent === "function" ? isRequestCurrent : () => true;
  const normalize = typeof normalizeSport === "function"
    ? normalizeSport
    : (value) => String(value || "").toLowerCase();
  const initialLimit = typeof getInitialAthleteLimit === "function" ? getInitialAthleteLimit : () => 0;
  const verifiedStats = typeof getVerifiedStats === "function" ? getVerifiedStats : () => ({});
  const currentSports = typeof getCurrentSports === "function" ? getCurrentSports : () => [];

  const resolveSelection = async ({
    requestToken,
    requestedSport,
    preferredStat = "",
    knownStats = null,
    reuseRecent = false,
  } = {}) => {
    const startedAt = now();
    const normalizedRequestedSport = normalize(requestedSport);
    const diagnostics = {
      startedAt,
      metadataMs: 0,
      athleteMs: 0,
      probeCount: 0,
      reconciled: false,
      verifiedCacheHit: false,
      cacheStatus: 0,
    };
    const completeDiagnostics = (extra = {}) => ({
      ...diagnostics,
      totalMs: Math.max(0, now() - startedAt),
      ...extra,
    });
    if (!normalizedRequestedSport || !isCurrent(requestToken)) {
      return { outcome: "aborted", diagnostics: completeDiagnostics() };
    }

    try {
      let targetSport = normalizedRequestedSport;
      let targetStats = knownStats && typeof knownStats === "object" ? knownStats : {};
      let targetStat = String(preferredStat || "");
      let selectionMetadata = null;

      const loadSelectionMetadata = async (sportKey, statKey) => {
        const metadataStartedAt = now();
        const metadata = await fetchStatsSelection(sportKey, statKey, {
          includeAthletes: true, limit: initialLimit(statKey) || 15,
        });
        diagnostics.metadataMs += Math.max(0, now() - metadataStartedAt);
        return metadata;
      };

      const applySelectionMetadata = (metadata) => {
        if (!metadata || typeof metadata !== "object") {
          throw new Error("The board selection response was empty.");
        }
        if (metadata.requestedSportAvailable === false && metadata.complete === false) {
          throw new Error("The requested league could not be verified yet.");
        }

        selectionMetadata = metadata;
        targetSport = normalize(metadata.sport || targetSport);
        targetStats = metadata.stats && typeof metadata.stats === "object"
          ? metadata.stats
          : (verifiedStats()?.[targetSport] || {});
        targetStat = String(metadata.stat || targetStat || "");
      };

      if (!targetStat || !Object.prototype.hasOwnProperty.call(targetStats, targetStat)) {
        applySelectionMetadata(await loadSelectionMetadata(targetSport, targetStat));
        if (!isCurrent(requestToken)) {
          return { outcome: "aborted", diagnostics: completeDiagnostics() };
        }
      }

      const probeDestination = async () => {
        const candidateStats = buildAtomicBoardStatCandidates(
          targetStats,
          targetStat,
          maxStatProbes,
          excludedStats
        );
        if (candidateStats.length === 0) {
          return { result: null, stat: "", failed: false, aborted: false };
        }

        for (const candidateStat of candidateStats) {
          const athleteStartedAt = now();
          const limit = initialLimit(candidateStat);
          diagnostics.cacheStatus = 0;
          const cached = reuseRecent && !selectionMetadata && candidateStat === preferredStat
            ? getRecentVerifiedAthletes(targetSport, candidateStat, limit, (status) => { diagnostics.cacheStatus = status; })
            : null;
          const athleteResult = cached || await fetchAthletes(
            targetSport,
            candidateStat,
            false,
            requestToken,
            { cache: false, ...(limit > 0 ? { limit } : {}),
              ...(selectionMetadata?.initialAthletes?.sport === targetSport &&
                selectionMetadata?.initialAthletes?.stat === candidateStat &&
                selectionMetadata?.initialAthletes?.limit === limit &&
                Array.isArray(selectionMetadata?.initialAthletes?.rows)
                ? { initialResponse: selectionMetadata.initialAthletes.rows } : {}) }
          );
          diagnostics.verifiedCacheHit = Boolean(cached);
          diagnostics.athleteMs += Math.max(0, now() - athleteStartedAt);
          diagnostics.probeCount += 1;

          if (athleteResult?.aborted || !isCurrent(requestToken)) {
            return { result: null, stat: "", failed: false, aborted: true };
          }
          if (athleteResult?.failed) {
            return { result: null, stat: "", failed: true, aborted: false };
          }
          if (Array.isArray(athleteResult?.list) && athleteResult.list.length > 0) {
            return { result: athleteResult, stat: candidateStat, failed: false, aborted: false };
          }
        }

        return { result: null, stat: "", failed: false, aborted: false };
      };

      let probe = await probeDestination();
      if (probe.aborted) {
        return { outcome: "aborted", diagnostics: completeDiagnostics() };
      }

      if (!probe.result && !probe.failed && !selectionMetadata) {
        diagnostics.reconciled = true;
        applySelectionMetadata(await loadSelectionMetadata(normalizedRequestedSport, targetStat));
        if (!isCurrent(requestToken)) {
          return { outcome: "aborted", diagnostics: completeDiagnostics() };
        }

        // Fresh metadata is authoritative even when the preferred league/stat
        // stays the same: it may expose a replacement market absent from the
        // cached map. Re-probe the bounded candidate set once.
        probe = await probeDestination();
        if (probe.aborted) {
          return { outcome: "aborted", diagnostics: completeDiagnostics() };
        }
      }

      if (!probe.result) {
        return {
          outcome: probe.failed ? "failed" : "empty",
          requestedSport: normalizedRequestedSport,
          targetSport,
          targetStat,
          diagnostics: completeDiagnostics(),
        };
      }

      if (!isCurrent(requestToken)) {
        return { outcome: "aborted", diagnostics: completeDiagnostics() };
      }

      const existingSports = currentSports();
      const metadataSports = selectionMetadata?.complete !== false && Array.isArray(selectionMetadata?.sports)
        ? selectionMetadata.sports
        : existingSports;
      const nextSports = Array.from(new Set([
        ...metadataSports,
        targetSport,
      ].filter(Boolean)));
      const availableStats = {
        ...(verifiedStats() || {}),
        [targetSport]: targetStats,
      };
      const snapshot = createBoardSnapshot({
        requestToken,
        requestedSport: normalizedRequestedSport,
        requestedStat: preferredStat,
        sport: targetSport,
        stat: probe.stat,
        stats: targetStats,
        sports: selectionMetadata?.complete !== false && Array.isArray(selectionMetadata?.sports)
          ? nextSports
          : Array.from(new Set([...(existingSports || []), targetSport].filter(Boolean))),
        availableStats,
        athletes: probe.result.list,
        metadataComplete: selectionMetadata?.complete === true,
        now: now(),
      });

      return {
        outcome: "ready",
        snapshot,
        requestedSport: normalizedRequestedSport,
        targetSport,
        targetStat,
        committedStat: probe.stat,
        athleteResult: probe.result,
        diagnostics: completeDiagnostics({
          feedEvidenceCount: snapshot.evidence.feedSnapshots.length,
        }),
      };
    } catch (error) {
      return {
        outcome: isCurrent(requestToken) ? "failed" : "aborted",
        error,
        requestedSport: normalizedRequestedSport,
        diagnostics: completeDiagnostics(),
      };
    }
  };

  return Object.freeze({ resolveSelection });
};

module.exports = {
  BOARD_SNAPSHOT_VERSION,
  createRecentVerifiedBoardCache,
  collectBoardFeedEvidence,
  createBoardDataSource,
  createBoardSnapshot,
  getBoardSnapshotExpiresAt,
};
